/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Version Info Component - Displays app build ID with details
 *
 * Shows current build ID with optional tooltip displaying:
 * - Build time
 * - Update priority
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useState, useEffect, useRef } from 'react'
import { versionConfig } from '../config/versionConfig'
import { createLogger } from '../utils/logger'

const logger = createLogger('VersionInfo')

const VersionInfo = ({ className = '' }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [metaData, setMetaData] = useState(null)
  const tooltipRef = useRef(null)
  const triggerRef = useRef(null)

  // Fetch meta.json for build info
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const response = await fetch('/meta.json', {
          cache: 'no-store'
        })
        if (response.ok) {
          const data = await response.json()
          setMetaData(data)
        }
      } catch (error) {
        // Silently fail - not critical
        logger.debug('[VersionInfo] Could not fetch meta.json:', error)
      }
    }

    fetchMeta()
  }, [])

  // Close tooltip on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        !triggerRef.current?.contains(event.target)
      ) {
        setShowTooltip(false)
      }
    }

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTooltip])

  const buildId = metaData?.buildId || versionConfig.buildId || 'development'

  // Format build time
  const formatBuildTime = (isoString) => {
    if (!isoString) return null
    try {
      const date = new Date(isoString)
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return null
    }
  }

  const buildTimeFormatted = formatBuildTime(metaData?.buildTime || versionConfig.buildTime)

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Build ID trigger */}
      <button
        ref={triggerRef}
        onClick={() => setShowTooltip(!showTooltip)}
        className="font-mono text-xs transition-colors text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer"
        title="Click for build details"
      >
        {buildId}
      </button>

      {/* Tooltip popup */}
      {showTooltip && (
        <div
          ref={tooltipRef}
          className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
        >
          <div className="space-y-2 text-sm">
            {/* Build ID */}
            <div className="flex justify-between items-center">
              <span className="text-gray-500 dark:text-gray-400">Build</span>
              <span className="font-mono font-medium text-gray-900 dark:text-gray-100">
                {buildId}
              </span>
            </div>

            {/* Build time */}
            {buildTimeFormatted && (
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Built</span>
                <span className="text-gray-900 dark:text-gray-100">{buildTimeFormatted}</span>
              </div>
            )}

            {/* Priority badge */}
            {metaData?.updatePriority && metaData.updatePriority !== 'recommended' && (
              <div className="pt-1 border-t border-gray-100 dark:border-gray-700">
                <span
                  className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                    metaData.updatePriority === 'critical'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  }`}
                >
                  {metaData.updatePriority}
                </span>
              </div>
            )}
          </div>

          {/* Arrow */}
          <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-white dark:bg-gray-800 border-r border-b border-gray-200 dark:border-gray-700" />
        </div>
      )}
    </div>
  )
}

export default VersionInfo
